import { useEffect, useMemo, useState } from 'react';
import { LobbyTypeEnum } from '@sportspredict/shared';
import { SWIPE_ONBOARDING_SEEN_KEY, SWIPE_ONBOARDING_STEPS } from '@/constants';

export function useOnboardingSwipe({
	lobby,
	isBingoLobby,
}: {
	lobby: any;
	isBingoLobby?: boolean;
}) {
	const [isOnboarding, setIsOnboarding] = useState(() => {
		if (typeof window === 'undefined') return false;
		if (lobby?.type === LobbyTypeEnum.CrowdLock) return false;

		try {
			const seen = window.localStorage.getItem(SWIPE_ONBOARDING_SEEN_KEY);
			return !seen;
		} catch {
			return true;
		}
	});
	const [onboardingStepIndex, setOnboardingStepIndex] = useState(0);

	// When a bingo lobby loads its data after the lazy initializer already ran,
	// re-check whether the animation should play (user switching from bingo → classic).
	useEffect(() => {
		if (!isBingoLobby) return;
		if (lobby?.type === LobbyTypeEnum.CrowdLock) return;

		try {
			const seen = window.localStorage.getItem(SWIPE_ONBOARDING_SEEN_KEY);
			if (!seen) setIsOnboarding(true);
		} catch {
			setIsOnboarding(true);
		}
	}, [isBingoLobby, lobby?.type]);

	const handleOnboardingClose = () => {
		try {
			if (typeof window !== 'undefined') {
				window.localStorage.setItem(SWIPE_ONBOARDING_SEEN_KEY, 'true');
			}
		} finally {
			setIsOnboarding(false);
			setOnboardingStepIndex(0);
		}
	};

	useEffect(() => {
		if (!isOnboarding) return;

		const timer = setInterval(() => {
			setOnboardingStepIndex((prev) => {
				const isLast = prev >= SWIPE_ONBOARDING_STEPS.length - 1;
				if (isLast) {
					clearInterval(timer);
					handleOnboardingClose();
					return prev;
				}
				return prev + 1;
			});
		}, 1666); // 5s total / 3 steps

		return () => clearInterval(timer);
	}, [isOnboarding]);

	const demoDirection = useMemo(() => {
		if (!isOnboarding) return null;
		const step = SWIPE_ONBOARDING_STEPS[onboardingStepIndex];
		if (!step) return null;

		if (step.key === 'yes') return 'right';
		if (step.key === 'no') return 'left';
		if (step.key === 'skip') return 'down';
		return null;
	}, [isOnboarding, onboardingStepIndex]);

	const demoNonce = onboardingStepIndex;

	return {
		isOnboarding,
		setIsOnboarding,
		onboardingStepIndex,
		setOnboardingStepIndex,
		demoDirection,
		demoNonce,
		handleOnboardingClose,
	};
}
